import { css } from '@emotion/react'
import { Featured } from '../../../types'
import { fetchFeaturedList } from '@/lib/featured'
import { colors, mq, breakpoint, font } from 'plume-ui'
import { featuredHref, featuredPath } from '@/lib/url'
import Link from 'next/link'
import { ContentfulResponsiveImage } from './ContentfulResponsiveImage'
import Slider from 'react-slick'
import React, { useEffect, useState } from 'react'

type Props = {
  className?: string
}

export const FeaturedSlider: React.FC<Props> = (props) => {
  const [featuredList, setFeaturedList] = useState<Featured[]>([])
  useEffect(() => {
    fetchFeaturedList().then((res) => {
      if (res) {
        setFeaturedList(res)
      }
    })
  }, [])

  return (
    <div
      id="featured"
      css={css`
        ${mq.large} {
          padding-top: 56px;
        }
      `}
    >
      <div
        css={css`
          background-color: ${colors.gray90};
          color: ${colors.white00};
          flex-direction: column;
          align-items: center;
          padding: 24px 0 56px 0;
          ${mq.large} {
            margin: 0 auto 56px;
            padding: 56px 0 64px;
          }
        `}
        className={props.className}
      >
        <div
          css={css`
            display: none;
            ${mq.large} {
              display: block;
              font-size: 14px;
              font-weight: ${font.weight.bold};
              line-height: 1.5;
              letter-spacing: 0.1em;
              text-align: center;
              margin-bottom: 0;
            }
          `}
        >
          FEATURED
        </div>
        <div
          css={css`
            margin-left: 24px;
            font-size: 20px;
            font-weight: ${font.weight.bold};
            line-height: 1.5;
            letter-spacing: 0.02em;
            ${mq.large} {
              margin-left: 0;
              font-size: 28px;
              text-align: center;
              letter-spacing: 0.01em;
            }
          `}
        >
          これまでの特集
        </div>
        <Slider
          infinite={false}
          arrows={false}
          dots={false}
          rows={Math.ceil(featuredList.length / 3)}
          slidesPerRow={3}
          swipe={false}
          responsive={[
            {
              breakpoint: breakpoint.large,
              settings: {
                slidesToShow: 1,
                rows: 1,
                slidesPerRow: 1,
                slidesToScroll: 1,
                dots: true,
                swipe: true,
                swipeToSlide: true,
                variableWidth: true,
              },
            },
          ]}
          css={css`
            margin-top: 16px;
            ${mq.large} {
              max-width: 1112px;
              margin: 8px auto 0;
            }
            .slick-list {
              ${mq.large} {
                overflow: visible;
              }
            }
            .slick-slide {
              ${mq.large} {
                > div {
                  display: flex;
                  margin-top: 24px;
                }
              }
            }
            .slick-dots {
              bottom: -36px;
              > li {
                margin: 0;
                > button::before {
                  left: auto;
                  top: auto;
                  content: '';
                  width: 8px;
                  height: 8px;
                  background-color: ${colors.gray50};
                  border-radius: 100%;
                  transform: translate(-50%, -50%);
                  opacity: 1;
                }
                &.slick-active > button::before {
                  background-color: ${colors.gray90} !important;
                }
              }
            }
          `}
        >
          {featuredList.map((featured) => (
            <FeaturedSlide
              key={featured.id}
              featured={featured}
            ></FeaturedSlide>
          ))}
        </Slider>
      </div>
    </div>
  )
}

const FeaturedSlide: React.FC<{ featured: Featured }> = ({ featured }) => {
  return (
    <Link
      href={featuredHref(featured.slug)}
      as={featuredPath(featured.slug)}
      passHref
    >
      <a
        css={css`
          display: flex;
          flex-direction: column;
          position: relative;
          background: ${colors.white00};
          border: 1px solid ${colors.border};
          border-radius: 4px;
          width: 288px;
          margin: 0 8px 8px;
          padding: 16px;
          [data-index='0'] & {
            margin-left: 24px;
          }
          &::before,
          &::after {
            position: absolute;
            content: '';
            display: table-cell;
            width: 100%;
            height: 100%;
            background: ${colors.white00};
            border: 1px solid ${colors.border};
            border-radius: 4px;
          }
          &::before {
            left: 5px;
            top: 5px;
            z-index: -1;
          }
          &::after {
            left: 9px;
            top: 9px;
            z-index: -2;
          }
          &:hover {
            border-color: ${colors.gray70};
            &::before,
            &::after {
              border-color: ${colors.gray70};
            }
          }
          ${mq.large} {
            display: grid;
            grid-template-columns: 1fr 112px;
            flex-basis: 352px;
            margin: 0 24px 0 0;
            padding: 20px 24px 24px;
            [data-index='0'] & {
              margin-left: 0;
            }
            &:nth-of-type(3) {
              margin-right: 0;
            }
          }
        `}
      >
        <p
          css={css`
            order: 2;
            margin: 8px 0 0;
            color: ${colors.textStrong};
            font-size: 16px;
            font-weight: ${font.weight.bold};
            line-height: 1.5;
            letter-spacing: 0.02em;
            ${mq.large} {
              order: inherit;
              grid-column-start: 1;
              grid-column-end: 3;
              margin: 0;
            }
          `}
        >
          {featured.title}
        </p>
        <p
          css={css`
            order: 3;
            display: -webkit-box;
            margin: 4px 0 0;
            color: ${colors.text};
            font-size: 12px;
            line-height: 1.5;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            ${mq.large} {
              order: inherit;
              margin-right: 8px;
            }
          `}
        >
          {featured.description}
        </p>
        <div
          css={css`
            order: 1;
            ${mq.large} {
              order: inherit;
              flex-shrink: 0;
            }
          `}
        >
          <ContentfulResponsiveImage
            src={featured.ogpImage.url}
          ></ContentfulResponsiveImage>
        </div>
      </a>
    </Link>
  )
}
